import {
  AloftPrice,
  CraftsmanAdjustmentProps,
  SalesComparisonApproachType,
  applyRoundingAndThresholds,
  getCraftsmanAdjustment,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectCompTypes,
  selectSalesComparisonApproach,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectSubjectUnitRateForFeature } from '../propertyDataSelectors/unitRateSelectors.ts';
import { selectDepreciationRatiosForFeature } from '../selectDepreciationRatioForFeature.ts';
import { selectDwellingCostNew, selectDwellingCostNews } from '../selectDwellingCostNew.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';
import { calculateUnitRateAdjustment } from './utils/calculateUnitRateAdjustment.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

// Adjustment reflects the comp relative to the subject: negative when the
// comp's condition rating is superior (a lower C number) to the subject's,
// positive when the comp is inferior (a higher C number).
const calculateConditionUnitRateAdj = (
  improvements: ReturnType<typeof selectImprovementsEntities>,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
  unitRate?: AloftPrice,
) => {
  const compType = compTypes[compID];
  const subjectImprovements =
    compType === 'arv' || compID === 'subjectArv' ? improvements.subjectArv : improvements.subject;

  const compImprovements = improvements[compID];

  return calculateUnitRateAdjustment({
    unitRate,
    subjectUnitAmount: compImprovements?.uadCondition,
    compUnitAmount: subjectImprovements?.uadCondition,
  });
};

const calculateConditionAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  compDwellingCostNew: AloftPrice | undefined,
  depreciationRatios: ReturnType<typeof selectDepreciationRatiosForFeature>,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
  salesComparisonApproach: SalesComparisonApproachType,
) => {
  // Condition's craftsman depreciation-based calculation only applies outside of
  // unit_rate mode; see calculateConditionUnitRateAdj / getConditionAdj for the
  // unit_rate math.
  if (salesComparisonApproach === 'unit_rate') return;
  if (!compDwellingCostNew || !depreciationRatios) return;

  const depreciationRatio =
    compTypes[compID] === 'arv' ? depreciationRatios.subjectArv : depreciationRatios.subject;

  const compDepreciationRatio = depreciationRatios[compID];

  if (isUndefined(depreciationRatio) || isUndefined(compDepreciationRatio)) return;

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);
  return adjCalculator.getConditionAdj({
    comp: {
      replacementCostNew: compDwellingCostNew,
    },
    depreciationRatio,
    compDepreciationRatio,
  }).adjustment;
};

const getConditionAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  compDwellingCostNew: AloftPrice | undefined,
  depreciationRatios: ReturnType<typeof selectDepreciationRatiosForFeature>,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
  salesComparisonApproach: SalesComparisonApproachType,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  unitRate?: AloftPrice,
) => {
  if (salesComparisonApproach === 'unit_rate') {
    return calculateConditionUnitRateAdj(improvements, compID, compTypes, unitRate);
  }

  return calculateConditionAdj(
    adjustmentProps,
    compDwellingCostNew,
    depreciationRatios,
    compID,
    compTypes,
    salesComparisonApproach,
  );
};

const selectConditionAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectDwellingCostNew,
    (state) => selectDepreciationRatiosForFeature(state, undefined),
    (_state, id: string) => id,
    selectCompTypes,
    selectSalesComparisonApproach,
    selectImprovementsEntities,
    (state) => selectSubjectUnitRateForFeature(state, 'condition'),
  ],
  getConditionAdj,
);

export const selectConditionAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectDwellingCostNews,
    (state) => selectDepreciationRatiosForFeature(state, undefined),
    selectCompSequence,
    selectCompTypes,
    selectSalesComparisonApproach,
    selectImprovementsEntities,
    (state) => selectSubjectUnitRateForFeature(state, 'condition'),
  ],
  (
    adjustmentProps,
    dwellingCostNews,
    depreciationRatios,
    compSequence,
    compTypes,
    salesComparisonApproach,
    improvements,
    unitRate,
  ) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = getConditionAdj(
        adjustmentProps,
        dwellingCostNews[propID],
        depreciationRatios,
        propID,
        compTypes,
        salesComparisonApproach,
        improvements,
        unitRate,
      );

      if (adj) acc[propID] = adj;
      return acc;
    }, {}),
);

const selectAppliedConditionAdj = createPriceSelector(
  [selectConditionAdj, selectAdjustmentSettings],
  (adj, adjSettings) => applyRoundingAndThresholds(adj, 'condition', adjSettings),
);

export const selectAppliedConditionAdjDisplay = createSelector(
  [selectAppliedConditionAdj],
  (adj) => {
    if (!adj) return;

    return {
      value: getStringValueFromAdjustment(adj),
      color: getColorFromAdjustment(adj),
    };
  },
);

export const selectAppliedConditionAdjsDisplay = createSelector(
  [
    selectCompSequence,
    selectConditionAdjs,
    selectAdjustmentSettings,
    selectManualAdjustmentsEntities,
  ],
  (compSequence, conditionAdjs, adjSettings, manualAdjustmentsEntities) =>
    compSequence.reduce((acc: Record<string, { value: string; color: string }>, propID) => {
      const adj = conditionAdjs[propID];
      const manualAdjustment = manualAdjustmentsEntities[propID]?.condition;

      if (manualAdjustment) {
        const manualAdjustmentPrice = AloftPrice.schema.parse(manualAdjustment);

        acc[propID] = {
          value: `${getStringValueFromAdjustment(manualAdjustmentPrice)}*`,
          color: getColorFromAdjustment(manualAdjustmentPrice),
        };
      } else if (adj) {
        acc[propID] = {
          value: getStringValueFromAdjustment(
            applyRoundingAndThresholds(adj, 'condition', adjSettings),
          ),
          color: getColorFromAdjustment(adj),
        };
      }

      return acc;
    }, {}),
);
